import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import useMeta from "@/lib/useMeta";
import { PageHeader, Empty } from "@/components/PageHeader";
import { OrgFormFields, orgToForm, formToOrgPayload } from "@/components/OrgFormFields";
import { UserPasswordActions } from "@/components/UserPasswordActions";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Loader2, ArrowLeft, Search, ExternalLink, Pencil, Trash2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { labelOf, SENIORITY } from "@/lib/jobFields";

export default function OrganizationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const meta = useMeta();
  const [org, setOrg] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(null);
  const [logins, setLogins] = useState([]);

  const load = () => api.get(`/organizations/${id}`).then(({ data }) => setOrg(data)).catch(() => {});
  const loadLogins = () => api.get("/admin/users", { params: { organization_id: id } }).then(({ data }) => setLogins(data.users || [])).catch(() => {});
  useEffect(() => { load(); loadLogins(); }, [id]);

  const openEdit = () => { setForm(orgToForm(org)); setEditOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/organizations/${id}`, formToOrgPayload(form));
      toast.success("Organization updated");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${org.name}"? This permanently removes the organization and all its searches, job postings, invoices and contracts. Linked user accounts are kept but detached. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/organizations/${id}`);
      toast.success("Organization deleted");
      navigate("/app/organizations");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDeleting(false); }
  };

  if (!org)
    return <div className="h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const roles = org.roles || [];
  const hasContact = org.primary_contact_name || org.primary_contact_email || org.primary_contact_phone;
  const hasNeed = roles.length > 0 || org.budget || org.notes;

  return (
    <div>
      <PageHeader overline="Organization" title={org.name}>
        <Button variant="ghost" onClick={openEdit} data-testid="org-edit-btn" className="rounded-none">
          <Pencil className="h-4 w-4 mr-1" /> Edit
        </Button>
        <Button variant="ghost" onClick={remove} disabled={deleting} data-testid="org-delete-btn" className="rounded-none text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10">
          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1" /> Delete</>}
        </Button>
        <Button variant="ghost" onClick={() => navigate("/app/organizations")} className="rounded-none">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </PageHeader>
      <div className="p-8 space-y-px bg-black/10">
        <div className="grid lg:grid-cols-3 gap-px">
          <div className="bg-white p-8 lg:col-span-1">
            <div className="h-16 w-16 bg-black/10 flex items-center justify-center font-heading font-black text-2xl mb-6">
              {org.name.charAt(0)}
            </div>
            <p className="overline">Type</p>
            <p className="font-medium capitalize mb-4">{(org.type || "").replace(/_/g, " ")}</p>
            {org.website && (
              <>
                <p className="overline">Website</p>
                <a href={org.website} target="_blank" rel="noreferrer" className="text-primary hover:underline flex items-center gap-1 mb-4">
                  {org.website} <ExternalLink className="h-3 w-3" />
                </a>
              </>
            )}
            <p className="overline">Industries</p>
            <div className="flex flex-wrap gap-1.5 mt-2 mb-4">
              {(org.industry_tags || []).map((t) => (
                <span key={t} className="text-[0.65rem] font-mono px-2 py-0.5 border border-black/10 text-muted-foreground">{t}</span>
              ))}
              {(!org.industry_tags || org.industry_tags.length === 0) && <span className="text-sm text-muted-foreground">—</span>}
            </div>

            {hasContact && (
              <>
                <p className="overline">Primary contact</p>
                <p className="font-medium mt-1">{org.primary_contact_name || "—"}</p>
                <div className="space-y-1 mt-1 text-sm">
                  {org.primary_contact_email && <p className="flex items-center gap-2 text-muted-foreground"><Mail className="h-3.5 w-3.5" /> {org.primary_contact_email}</p>}
                  {org.primary_contact_phone && <p className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5" /> {org.primary_contact_phone}</p>}
                </div>
              </>
            )}

            <div className="mt-6 pt-6 border-t border-black/10" data-testid="org-logins">
              <p className="overline">Client logins</p>
              {logins.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-2">No portal login accounts linked to this organization yet.</p>
              ) : (
                <div className="space-y-4 mt-3">
                  {logins.map((u) => (
                    <div key={u.id} data-testid={`org-login-${u.id}`}>
                      <p className="text-sm font-medium">{u.full_name || u.email}</p>
                      <p className="text-xs text-muted-foreground mb-2">{u.email}{u.must_change_password ? " · must reset" : ""}</p>
                      <UserPasswordActions userId={u.id} onDone={loadLogins} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white p-8 lg:col-span-2 space-y-8">
            {hasNeed && (
              <div data-testid="org-search-need">
                <p className="overline mb-4">Search need</p>
                <div className="grid grid-cols-3 gap-px bg-black/10 border border-black/10 mb-4">
                  <div className="bg-white p-4"><p className="overline text-[0.5rem]">Timeline</p><p className="text-sm mt-1 capitalize">{(org.timeline || "—").replace(/_/g, " ")}</p></div>
                  <div className="bg-white p-4"><p className="overline text-[0.5rem]">Engagement</p><p className="text-sm mt-1 capitalize">{org.engagement_type || "—"}</p></div>
                  <div className="bg-white p-4"><p className="overline text-[0.5rem]">Budget</p><p className="text-sm mt-1">{org.budget || "—"}</p></div>
                </div>
                {roles.length > 0 && (
                  <div className="space-y-px">
                    {roles.map((r, i) => (
                      <div key={i} className="flex items-center justify-between p-3 border border-black/10 text-sm">
                        <span className="font-medium">{r.title}</span>
                        <span className="overline text-[0.55rem]">{r.seniority ? labelOf(SENIORITY, r.seniority) : "—"}</span>
                      </div>
                    ))}
                  </div>
                )}
                {org.notes && <p className="text-sm text-muted-foreground leading-relaxed mt-4 border-l-2 border-black/10 pl-4">{org.notes}</p>}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-6">
                <p className="overline">Searches for this organization</p>
                <Button size="sm" onClick={() => navigate("/app/searches")} className="rounded-none bg-primary hover:bg-[#09090B] hover:text-white">
                  <Search className="h-3.5 w-3.5 mr-1" /> New search
                </Button>
              </div>
              {org.searches.length === 0 && <Empty icon={Building2} title="No searches yet" hint="Kick off a search for this client." />}
              <div className="space-y-px">
                {org.searches.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => navigate(`/app/searches/${s.id}`)}
                    className="w-full text-left p-4 border border-black/10 hover:bg-black/5 transition-colors flex items-center justify-between group"
                  >
                    <div>
                      <p className="font-medium group-hover:text-primary transition-colors">{s.title}</p>
                      <p className="overline text-[0.55rem] mt-1">{s.role_type} · {s.hiring_type} · {s.status}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{s.location || "—"}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#F4F4F5] border-black/10 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit Organization</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <OrgFormFields form={form} setForm={setForm} meta={meta} />
              <DialogFooter>
                <Button type="submit" data-testid="org-edit-save" disabled={saving} className="rounded-none bg-primary hover:bg-[#09090B] hover:text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
